import { Link, useLocation, useNavigate } from "react-router-dom";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  FileText,
  BookOpen,
  ClipboardList,
  Dumbbell,
  Users,
  Loader2,
  ShieldAlert,
} from "lucide-react";

const adminNav = [
  { icon: FileText, label: "Prompt Templates", to: "/admin/templates" },
  { icon: ClipboardList, label: "Questionnaires", to: "/admin/questionnaires" },
  { icon: BookOpen, label: "Knowledge Base", to: "/admin/knowledge" },
  { icon: Dumbbell, label: "Sports", to: "/admin/sports" },
  { icon: Users, label: "Users", to: "/admin/users" },
];

const AdminLayout = ({ children }: { children: React.ReactNode }) => {
  const { isAdmin, loading } = useIsAdmin();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen gradient-hero flex flex-col items-center justify-center gap-4">
        <ShieldAlert className="w-12 h-12 text-destructive" />
        <h1 className="font-display text-2xl font-bold">Access Denied</h1>
        <p className="text-muted-foreground">You need admin privileges to access this page.</p>
        <Button variant="outline" onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-hero">
      <header className="border-b border-border/50 bg-background/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="font-display text-xl font-bold">
            <span className="text-gradient-primary">Admin</span>
          </span>
          <nav className="flex items-center gap-1 ml-6">
            {adminNav.map((item) => {
              const isActive = location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="hidden md:inline">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </header>
      <main className="container px-6 py-8">{children}</main>
    </div>
  );
};

export default AdminLayout;
